import { useEffect, useMemo, useState } from "react"
import propTypes from "prop-types"
import { Formik } from "formik"
import { Alert, Button, Col, Form, Input, InputGroup, InputGroupText, 
          Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { Loading, RequiredIcon } from "components"
import ErrorsText from "components/ErrorsText"
import { addCashMovemnts, getCashMovemnts } from "services/tickets"
import {
  formatCurrency,
  getAdjustmentLimitStatus,
  getStoredAdjustmentLimit,
} from "utils/cash-movement-analysis"

const parseCashAmount = (value) => Number(String(value ?? "").replace(/,/g, ""))

const CashBalanceMovementModal = ({
  isOpen,
  toggle,
  title,
  accountLabel,
  userId,
  movementType,
  sourceAccountType,
  destinationAccountType,
  requiresReason,
  setShowAlertSuccess,
  setMsgSuccess,
  refetchBalances,
}) => {
  const { mutateAsync, isLoading, isError, error } = useMutation((payload) => addCashMovemnts(payload))
  const queryClient = useQueryClient()
  const hasUserId = Boolean(userId)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isAdjustment = movementType === "adjustment"
  const adjustmentLimit = getStoredAdjustmentLimit()
  const adjustmentMovementsQuery = useQuery(
    ["cash-movements-adjustment-limit"],
    () =>
      getCashMovemnts({
        ordering: "created_at",
        per_page: 200,
        offset: 0,
        movement_type: "adjustment",
      }),
    {
      enabled: isOpen && isAdjustment,
      refetchOnMount: "always",
      staleTime: 0,
    }
  )
  const isProcessing = isLoading || isSubmitting || (isAdjustment && adjustmentMovementsQuery.isFetching)

  const initialValues = useMemo(
    () => ({
      cash: "",
      reason: "",
    }),
    []
  )

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false)
    }
  }, [isOpen])

  const formatInputCurrency = (value) => {
    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return formatter.format(value)
  }

  const validate = (values) => {
    const errors = {}
    const cleanAmount = parseCashAmount(values.cash)
    const signedAmount = sourceAccountType && !destinationAccountType ? cleanAmount * -1 : cleanAmount
    const limitStatus = getAdjustmentLimitStatus(
      adjustmentMovementsQuery.data?.results || [],
      signedAmount,
      adjustmentLimit
    )

    if (!values.cash) {
      errors.cash = "La cantidad es obligatoria"
    } else if (Number.isNaN(cleanAmount) || cleanAmount <= 0) {
      errors.cash = "La cantidad debe ser mayor a 0"
    } else if (isAdjustment && limitStatus.isOutOfLimit) {
      errors.cash = `El acumulado de ajustes quedaria en ${formatCurrency(limitStatus.nextBalance)}. Limite: +/- ${formatCurrency(adjustmentLimit)}`
    }

    if (requiresReason && !String(values.reason || "").trim()) {
      errors.reason = "El motivo es obligatorio"
    }

    return errors
  }

  const submit = async (values, { resetForm }) => {
    if (!hasUserId) {
      return
    }

    const payload = {
      user: userId,
      cash: Number(String(values.cash).replace(/,/g, "")),
      movement_type: movementType,
      source_account_type: sourceAccountType || null,
      destination_account_type: destinationAccountType || null,
      reason: String(values.reason || "").trim() || null,
    }

    setIsSubmitting(true)
    try {
      await mutateAsync(payload)
      setMsgSuccess(`${title} realizado satisfactoriamente`)
      setShowAlertSuccess(true)
      await refetchBalances()
      // fast refresh: invalidate movements query so history updates immediately
      await queryClient.invalidateQueries(["cash-movements-history"])
      await queryClient.invalidateQueries(["cash-movements-adjustment-limit"])
      resetForm()
      toggle()
    } catch (submitError) {
      setIsSubmitting(false)
      return submitError
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={isProcessing ? undefined : toggle} centered size="md" backdrop="static">
      <ModalHeader toggle={isProcessing ? undefined : toggle} charcode="X">
        {title}
      </ModalHeader>
      <ModalBody>
        {isProcessing && (
          <div className="text-center py-4">
            <Loading isLoading={isProcessing} size={2} />
            <div className="mt-2">Procesando movimiento...</div>
          </div>
        )}
        {!isProcessing && (
          <>
        <Alert color="info" className="mb-3">
          Movimiento sobre: {accountLabel}
        </Alert>
        {!hasUserId && (
          <Alert color="warning" className="mb-3">
            No se pudo identificar el usuario actual. Espera unos segundos y vuelve a intentarlo.
          </Alert>
        )}
        <Formik enableReinitialize initialValues={initialValues} validate={validate} onSubmit={submit}>
          {({ values, handleChange, handleSubmit, handleBlur, touched, errors, setFieldValue }) => {
            const cleanAmount = parseCashAmount(values.cash)
            const signedAmount = sourceAccountType && !destinationAccountType ? cleanAmount * -1 : cleanAmount
            const adjustmentLimitStatus = getAdjustmentLimitStatus(
              adjustmentMovementsQuery.data?.results || [],
              signedAmount,
              adjustmentLimit
            )

            return (
              <Form className="form-horizontal" onSubmit={handleSubmit}>
                {isError && (
                  <Alert color="danger" isOpen={isError}>
                    {error?.errors?.map((item) => item?.message)}
                  </Alert>
                )}
                {isAdjustment && adjustmentLimitStatus.isOutOfLimit && (
                  <Alert color="danger">
                    El acumulado de ajustes quedaria en {formatCurrency(adjustmentLimitStatus.nextBalance)}.
                    Limite actual: +/- {formatCurrency(adjustmentLimit)}.
                  </Alert>
                )}
                <Row className="mb-3">
                  <Col>
                    <Label className="form-label">
                      Cantidad <RequiredIcon />
                    </Label>
                    <InputGroup>
                      <InputGroupText>$</InputGroupText>
                      <Input
                        autoComplete="off"
                        className="form-control"
                        type="text"
                        name="cash"
                        onBlur={(event) => {
                          const inputValue = event.target.value.replace(/,/g, "")
                          const formattedValue = formatInputCurrency(inputValue)
                          setFieldValue("cash", formattedValue)
                          handleBlur(event)
                        }}
                        onChange={handleChange}
                        value={values.cash}
                        invalid={!!(touched.cash && errors.cash)}
                      />
                    </InputGroup>
                    <ErrorsText errorsBack={error} errorsForm={errors} 
                      formName="cash" isErrorBack={isError} touched={touched} />
                  </Col>
                </Row>

                {requiresReason && (
                  <Row className="mb-3">
                    <Col>
                      <Label className="form-label">
                        Motivo <RequiredIcon />
                      </Label>
                      <Input
                        autoComplete="off"
                        className="form-control"
                        type="textarea"
                        name="reason"
                        onChange={handleChange}
                        value={values.reason}
                        invalid={!!(touched.reason && errors.reason)}
                      />
                      <ErrorsText
                        errorsBack={error}
                        errorsForm={errors}
                        formName="reason"
                        isErrorBack={isError}
                        touched={touched}
                      />
                    </Col>
                  </Row>
                )}

                <hr />

                <Row>
                  <Col className="d-flex justify-content-end">
                    <Button className="mx-3" color="secondary" onClick={toggle} disabled={isProcessing}>
                      <i className="fa fa-times" /> Cancelar
                    </Button>
                    <Button color="success" type="submit" disabled={isProcessing || !hasUserId}>
                      <i className="fa fa-save" /> Confirmar
                    </Button>
                  </Col>
                </Row>
              </Form>
            )
          }}
        </Formik>
          </>
        )}
      </ModalBody>
    </Modal>
  )
}

CashBalanceMovementModal.propTypes = {
  isOpen: propTypes.bool,
  toggle: propTypes.func,
  title: propTypes.string,
  accountLabel: propTypes.string,
  userId: propTypes.any,
  movementType: propTypes.string,
  sourceAccountType: propTypes.string,
  destinationAccountType: propTypes.string,
  requiresReason: propTypes.bool,
  setShowAlertSuccess: propTypes.func,
  setMsgSuccess: propTypes.func,
  refetchBalances: propTypes.func,
}

export default CashBalanceMovementModal
